import style from "@/styles/pages/SelectApplicationType/SelectApplicationType.module.css";
import { FormProvider, set, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    selectHomeAddress,
} from "@/slices/homeAddressSlice";
import {
    selectMailingAddress,
} from "@/slices/mailingAddressSlice";
import { setCredentialsComplete } from "@/slices/progressSlice";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import ExistingMemberCard from "@/components/GetStarted/ExistingMemberCard";
import { setIsProspect } from "@/slices/applicantSlice";
import { setApplicationCategory, setApplicationProgress } from "@/slices/applicationSlice";
import { applicationCategory } from "@/types/applicationType";
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import clsx from "clsx";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors, setSingleClickInsightEvent } from "@/lib/util";

export default function SelectApplicationType() {
    const navigate = useNavigate();

    const [applicationType, setApplicationType] = useState(0);
    const [displayError, setDisplayError] = useState(false);

    const dispatch = useDispatch();

    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const legalTerms = useSelector(
        (state: RootState) => state.legalTerms
      );
    const employment = useSelector(
        (state: RootState) => state.employment.employment
      );
    const twilioResult = useSelector(
        (state: RootState) => state.applicant.applicant.twilioResult
    );
    const credential = useSelector(
        (state: RootState) => state.credential
      );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    const homeAddress = useSelector(selectHomeAddress);
    const mailingAddress = useSelector(selectMailingAddress);

    const methods = useForm({
        defaultValues: {
            autoAddress: "",
            autoMailAddress: "",
            streetNumber: homeAddress.address.streetName,
            streetName: homeAddress.address.streetNumber,
            unitNumber: homeAddress.address.unitNumber,
            cityName: homeAddress.address.city,
            provinceName: homeAddress.address.province,
            postalCode: homeAddress.address.postalCode,
            country: homeAddress.address.country,
            isMailingAddressSame: true,
            maStreetNumber: mailingAddress.address.streetNumber,
            maStreetName: mailingAddress.address.streetName,
            maCityName: mailingAddress.address.city,
            maUnitNumber: mailingAddress.address.unitNumber,
            maProvinceName: mailingAddress.address.province,
            maPostalCode: mailingAddress.address.postalCode,
            maCountry: mailingAddress.address.country,
        },
    });

    useEffect(() => {
        document.title = "How can Meridian help?";
        dispatch(setApplicationProgress(0));
        //*set profile section complete
        dispatch(setCredentialsComplete(true));
        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

        switch (application.applicationCategory) {
            case applicationCategory.Buy:
                setApplicationType(1);
                break;
            case applicationCategory.Refinance:
                setApplicationType(2);
                break;
            case applicationCategory.Transfer:
                setApplicationType(3);
                break;
        }
        if (!credential.credential.email.includes("@")) {
            dispatch(setIsProspect(false));
        }

        client.apiApplicationsFetch().then((result) => {
            result.application = application;
            result.applicant = applicant;
            result.employment = employment;
            result.legalTerms = legalTerms;
            result.referralProducts = referralProducts;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info", error);
        });

    }, []);

    const onError = (errors: object) => {
        console.log(errors);
        setClickInsightFormErrors(errors);
    }

    const onSubmit = (data: any) => {
        if (applicationType === 0) {
            setDisplayError(true);
            return;
        }

        dispatch(setApplicationProgress(1));
        setClickInsightApplicationProgress('Your new mortgage - How can Meridian help?');
        switch (applicationType) {
            case 1:
                dispatch(setApplicationCategory(applicationCategory.Buy));
                navigate("/single/select-assessment");
                break;
            case 2:
                dispatch(setApplicationCategory(applicationCategory.Refinance));
                navigate("/single/property-value");
                break;
            case 3:
                dispatch(setApplicationCategory(applicationCategory.Transfer));
                navigate("/single/property-value");
                break;
            case 4:
                navigate("/single/confirm-bridge-loan");
                break;
        }
    };

    const handleClick = (type: number) => {
        setApplicationType(type);
        setDisplayError(false);
        setSingleClickInsightEvent('applicationTypeSelected', undefined, type == 1 ? 'Buy a property' : type == 2 ? 'Refinance my mortgage' : 'Transfer my mortgage');
    }

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <div className="max-md:ml-4">
                            <h1 aria-label="Address Header">How can Meridian help?</h1>
                            <p className="sub-heading">Tell us what we can help you with today.</p>
                        </div>
                        {displayError &&
                            <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                                <img src="assets/error-info-icon.svg" alt="error" />
                                <span className="text-sm ml-2 mt-1">
                                    Please select an option to continue.
                                </span>
                            </div>
                        }
                        <div className={clsx("flex flex-row", displayError ? "" : "mt-5")}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className="content-container">
                                        <div className="grid grid-cols-2 md:grid-cols-2 xlg:grid-cols-4">
                                            <div className="flex-row mb-5 sm:w-full">
                                                <div onClick={() => handleClick(1)}>
                                                    <ExistingMemberCard
                                                        cardHeight={200}
                                                        cardWidth={215}
                                                        imageSrc="assets/buy-property.svg"
                                                        cardText="Buy a property"
                                                        selected={applicationType == 1}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex-row mb-5 sm:w-full">
                                                <div onClick={() => handleClick(2)}>
                                                    <ExistingMemberCard
                                                        cardHeight={200}
                                                        cardWidth={215}
                                                        imageSrc="assets/debt-consolidation.svg"
                                                        cardText="Refinance my mortgage"
                                                        selected={applicationType == 2}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex-row mb-5 sm:w-full">
                                                <div onClick={() => handleClick(3)}>
                                                    <ExistingMemberCard
                                                        cardHeight={200}
                                                        cardWidth={215}
                                                        imageSrc="assets/transfer-mortgage.svg"
                                                        cardText="Transfer my mortgage"
                                                        selected={applicationType == 3}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-5">
                                        <button
                                            type="submit"
                                            className="primary mt-5 w-full sm:w-28 h-11 px-6">
                                            Continue
                                        </button>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}